// ParagraphRenderer.jsx - Refactored to optimize re-renders
import React from 'react';
import ParagraphContainer from './ParagraphContainer';
import useSelectionData from 'hooks/useSelectionData';

// Generic utility function to check if two ranges overlap
const isOverlap = (start1, end1, start2, end2) => {
  // Reject non-finite bounds (undefined OR NaN). A NaN bound makes every
  // comparison below false, which would invert to "overlaps everything" and
  // apply a bogus selection to every paragraph (blanking their text).
  if (![start1, end1, start2, end2].every(Number.isFinite)) {
    return false;
  }
  // Two ranges overlap if it's NOT the case that one ends before the other starts
  return !(start1 >= end2 || end1 <= start2);
};

// Shared function to determine if a paragraph should receive selection data
const getParagraphSelection = (paragraph, selectionData) => {
  if (!selectionData) return null;

  const { selectedExcerptId, selectionStart, selectionEnd } = selectionData;

  // If we have selection boundaries, check if this paragraph overlaps with it
  if (selectionStart !== undefined && selectionEnd !== undefined) {
    return isOverlap(selectionStart, selectionEnd, paragraph.start, paragraph.end)
      ? selectionData
      : null;
  }

  return null;
};

const ParagraphRenderer = ({
  visibleParagraphs = [],
  speakerColorHash,
  transcriptID,
  select,
  selectedExcerptId,
  // Exact props that ParagraphContainer expects
  canEdit = false,
  isPrint = true,
  hasEditPermission = false,
  marginLeft,
  marginRight = "20px",
  // Layout control
  layout = 'single', // 'single' | 'sideBySide'
  // TranscriptRenderer specific props
  onParagraphClick = null,
  setParagraphRef = null,
  // Filtering props
  leftUserID = null,
  // Comparison page code editing
  showCodeSelector = false,
  enableHover = false
}) => {
  if (!visibleParagraphs || !visibleParagraphs.length) return null;

  // Use the shared selection hook to create selection data
  const selectionData = useSelectionData(
    selectedExcerptId,
    select,
    transcriptID,
    'Transcript'
  );

  // Single column rendering (TranscriptRenderer and ComparisonViewSolo)
  if (layout === 'single') {
    return visibleParagraphs.map((paragraph, index) => {
      if (!paragraph || paragraph.start === paragraph.end) return null;

      // Only pass selection data if this paragraph is affected by the selection
      const paragraphSelection = getParagraphSelection(paragraph, selectionData);

      const containerElement = (
        <ParagraphContainer
          key={`${transcriptID}_${paragraph.paragraphIndex}`}
          paragraph={paragraph}
          speakerColorHash={speakerColorHash}
          selection={paragraphSelection}
          absoluteIndex={paragraph.paragraphIndex}
          paragraphIndex={index}
          transcriptID={transcriptID}
          codeableType="Transcript"
          canEdit={canEdit}
          isPrint={isPrint}
          hasEditPermission={hasEditPermission}
          marginLeft={marginLeft}
          marginRight={marginRight}
          onParagraphClick={onParagraphClick}
          filterUserID={leftUserID}
          showCodeSelector={showCodeSelector}
          enableHover={enableHover}
        />
      );

      // TranscriptRenderer needs ref wrapper
      if (setParagraphRef) {
        return (
          <div key={paragraph.paragraphIndex} ref={(pc) => setParagraphRef(index, pc)}>
            {containerElement}
          </div>
        );
      }

      return containerElement;
    }).filter(Boolean);
  }

  return null;
};

export default ParagraphRenderer;
